<template>
  <div>
    <h3 class="mb-3">
      Pending payouts ({{ formatMoney(totalPendingPayoutsAmount) }} €)
      <small class="text-muted fs-6 fw-normal" v-if="!showAllPendingPayouts">
        above {{ formatMoney(MINIMUM_PAYOUT_AMOUNT) }} €
      </small>
    </h3>
    <div class="card">
      <div class="card-body p-3">
        <LoadingIndicatorComponent v-if="isLoading" />

        <div
          v-else-if="displayedPendingPayouts.length === 0"
          class="alert alert-info mb-0"
        >
          No pending payouts.
        </div>

        <template v-else>
          <div
            class="mb-3"
            v-for="payout in displayedPendingPayouts"
            :key="payout.company_id"
          >
            <h5 class="mb-2 d-flex align-items-center flex-wrap gap-2">
              <span>
                {{
                  companies[payout.company_id]?.name ??
                  `Company ${payout.company_id}`
                }}
                ({{ formatMoney(payout.totalAmount) }} €)
              </span>
              <button
                @click="togglePendingCollapse(payout.company_id)"
                class="btn btn-sm btn-link"
              >
                <font-awesome-icon
                  :icon="
                    isPendingCollapsed(payout.company_id) ? 'eye' : 'eye-slash'
                  "
                />
              </button>
              <button
                class="btn btn-sm btn-subtle-primary ms-auto"
                title="Open this company's fee items for editing and settlement"
                @click="$emit('view:company', payout.company_id)"
              >
                <font-awesome-icon icon="file-invoice-dollar" class="me-1" />
                Manage fee items
              </button>
            </h5>
            <div class="card" v-if="!isPendingCollapsed(payout.company_id)">
              <div class="card-body p-3">
                <InvoiceItemDisplayComponent
                  v-for="item in payout.items"
                  :key="item.id"
                  :invoiceItem="item"
                />
              </div>
            </div>
          </div>

          <div class="text-center mt-3" v-if="hasMorePendingPayouts">
            <button
              class="btn btn-outline-secondary w-100"
              @click="showAllPendingPayouts = !showAllPendingPayouts"
            >
              {{
                showAllPendingPayouts
                  ? "Hide payouts below " +
                    formatMoney(MINIMUM_PAYOUT_AMOUNT) +
                    " €"
                  : "Show pending payouts below " +
                    formatMoney(MINIMUM_PAYOUT_AMOUNT) +
                    " €"
              }}
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import InvoiceItemDisplayComponent from "@/components/common/InvoiceItemDisplayComponent.vue";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { formatMoney } from "@/composables/formatUtils";
import { getPendingPayouts } from "@/composables/money/invoiceUtils";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { InvoiceItem } from "@/types";
import { computed, onMounted, ref } from "vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

defineEmits<{
  "view:company": [companyId: number];
}>();

const MINIMUM_PAYOUT_AMOUNT = 500;

const { globalErrorHandler } = useGlobalHandlers();
const companies = ref<Record<number, { id: number; name: string }>>({});
const pendingInvoiceItems = ref<InvoiceItem[]>([]);
const shownPendingPayouts = ref<Set<number>>(new Set());
const showAllPendingPayouts = ref(false);
const isLoading = ref(true);

const pendingPayouts = computed(() =>
  getPendingPayouts(pendingInvoiceItems.value)
);

const totalPendingPayoutsAmount = computed(() =>
  pendingPayouts.value.reduce((sum, payout) => sum + payout.totalAmount, 0)
);

const displayedPendingPayouts = computed(() => {
  const sortedPayouts = [...pendingPayouts.value].sort(
    (a, b) => b.totalAmount - a.totalAmount
  );

  if (showAllPendingPayouts.value) {
    return sortedPayouts;
  }

  return sortedPayouts.filter(
    (payout) => payout.totalAmount >= MINIMUM_PAYOUT_AMOUNT
  );
});

const hasMorePendingPayouts = computed(() =>
  pendingPayouts.value.some(
    (payout) => payout.totalAmount < MINIMUM_PAYOUT_AMOUNT
  )
);

const togglePendingCollapse = (companyId: number) => {
  if (shownPendingPayouts.value.has(companyId)) {
    shownPendingPayouts.value.delete(companyId);
  } else {
    shownPendingPayouts.value.add(companyId);
  }
};

const isPendingCollapsed = (companyId: number) => {
  return !shownPendingPayouts.value.has(companyId);
};

const fetchAllPendingInvoiceItems = async () => {
  try {
    const response = await goSolidApi.get("/api/admin/pending-invoice-items");
    if (response.data) {
      pendingInvoiceItems.value = response.data.invoice_items;
    } else {
      throw new Error("Error fetching invoice items.");
    }
  } catch (error: unknown) {
    globalErrorHandler(error);
  }
};

const fetchAllCompanies = async () => {
  try {
    const response = await goSolidApi.get("/api/admin/companies");
    if (response.data) {
      companies.value = response.data;
    } else {
      throw new Error("Error fetching companies.");
    }
  } catch (error: unknown) {
    globalErrorHandler(error);
  }
};

onMounted(async () => {
  try {
    await Promise.allSettled([
      fetchAllPendingInvoiceItems(),
      fetchAllCompanies(),
    ]);
  } finally {
    isLoading.value = false;
  }
});
</script>
